'use client';

import { useClientHasRendered } from '@/domain/session/useClientHasRendered';
import { getNodesFromConnection } from '@/graphql/connections/getNodesFromConnection';
import { Container } from '../../components/Container/Container';
import { HeroSection } from './components/HeroSection';
import { ProgressSection } from './components/ProgressSection';
import { CollectorsCarousel, Collector } from './components/CollectorsCarousel';
import { Leaderboard } from './components/Leaderboard';
import { useTrashCollectionStateLeaderboard } from './hooks/useTrashCollectionStateLeaderboard';
import { useTrashCollectionBrandLeaderboard } from './hooks/useTrashCollectionBrandLeaderboard';
import { useTrashCollections } from './hooks/useTrashCollections';
import { Source } from './helpers/getRoute';

const TOP_LEADERBOARD_ROWS = 5;
const COLLECTORS_PREVIEW_COUNT = 5;

interface IProps {
  source?: Source;
}

export const MillionPoundChallenge = ({ source = 'web' }: IProps) => {
  const clientHasRendered = useClientHasRendered();
  const { statesRows, isStatesLoading } = useTrashCollectionStateLeaderboard({
    skip: !clientHasRendered,
  });
  const { brandsRows, isBrandsLoading } = useTrashCollectionBrandLeaderboard({
    skip: !clientHasRendered,
  });
  const { data: trashData } = useTrashCollections({ skip: !clientHasRendered });

  const collectors: Collector[] = getNodesFromConnection(trashData?.trashCollections)
    .slice(0, COLLECTORS_PREVIEW_COUNT)
    .map(node => ({
      id: node.externalId,
      username: node.user.displayName,
      stateName: node.stateName ?? '',
      weightInKg: node.weightInKg,
      avatarUrl: node.user.avatar?.url,
      photoUrl: node.image.url,
      description: node.description ?? undefined,
    }));

  const collectedWeightInKgs = trashData?.trashCollections.totalWeightInKgs ?? 0;

  if (!clientHasRendered) {
    return null;
  }

  return (
    <Container className="flex flex-col gap-12 py-8">
      <HeroSection />
      <ProgressSection collectedWeightInKgs={collectedWeightInKgs} />
      <CollectorsCarousel collectors={collectors} source={source} />
      <Leaderboard
        statesRows={statesRows}
        isStatesLoading={isStatesLoading}
        brandsRows={brandsRows}
        isBrandsLoading={isBrandsLoading}
        limit={TOP_LEADERBOARD_ROWS}
      />
    </Container>
  );
};
